// Phase R8 — Slip Extractor's OWN Section-A Profile presentation.
//
// Engineering ruling (handoff Q): Profile stays in scope for the
// Extractor, keeps the SAME data source (useBettingProfile /
// BettingProfileFull), but gets its OWN presentation — it no longer
// borrows the slip-autopsy-2 panels (that visual reuse was a primary
// reason the two products looked identical).
//
// Visual port of the Claude Design `ProfileBand` (extractor-art-
// extras.jsx) onto se-* tokens. N11: in the Extractor surface every
// pattern alert is INFORMATIONAL (weight 0) — the scorer never uses
// loss history; this is shown, never used.

import type {
  Archetype,
  BettingProfileFull,
  ProfileConfidence,
} from "../../slips/useBettingProfile.ts";

const ARCHETYPE_LABEL: Record<Archetype, string> = {
  value_seeker: "Value seeker",
  jackpot_hunter: "Jackpot hunter",
  disciplined_compounder: "Disciplined compounder",
  intelligence_learner: "Intelligence learner",
};
const CONFIDENCE_LABEL: Record<ProfileConfidence, string> = {
  low: "Low",
  building: "Building",
  strong: "Strong",
  expert: "Expert",
};

function pctOf(v: number | null | undefined): string {
  return typeof v === "number" ? `${Math.round(v * 100)}%` : "—";
}

function ProfileFigure(
  { label, value, sub }: { label: string; value: string; sub?: string },
): JSX.Element {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div
        style={{
          fontFamily: "var(--se-font-mono)",
          fontSize: 10,
          letterSpacing: "0.22em",
          color: "var(--se-text-muted)",
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: "var(--se-font-display)",
          fontSize: 18,
          fontWeight: 500,
          color: "var(--se-text)",
        }}
      >
        {value}
      </div>
      {sub && (
        <div
          style={{
            fontFamily: "var(--se-font-mono)",
            fontSize: 10,
            color: "var(--se-text-muted)",
          }}
        >
          {sub}
        </div>
      )}
    </div>
  );
}

const COL_HEAD: React.CSSProperties = {
  fontFamily: "var(--se-font-mono)",
  fontSize: 9.5,
  letterSpacing: "0.22em",
  color: "var(--se-text-muted)",
  textTransform: "uppercase",
  padding: "14px 18px 10px",
};
const CELL: React.CSSProperties = {
  fontFamily: "var(--se-font-mono)",
  fontSize: 12,
  color: "var(--se-text-sec)",
};

function MarketMatrix(
  { data }: { data: BettingProfileFull["market_performance"] },
): JSX.Element {
  const rows = Object.entries(data).sort(
    (a, b) => (b[1].legs ?? 0) - (a[1].legs ?? 0),
  );
  return (
    <div
      data-testid="sle-section-a-a2"
      style={{ borderRight: "1px solid var(--se-border)" }}
    >
      <div style={COL_HEAD}>A2 · Market performance</div>
      {rows.length === 0 ? (
        <div style={{ ...CELL, padding: "0 18px 16px" }}>
          No tracked markets yet.
        </div>
      ) : (
        rows.slice(0, 8).map(([market, e]) => (
          <div
            key={market}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto auto",
              gap: 10,
              padding: "8px 18px",
              borderTop: "1px solid var(--se-border)",
              ...CELL,
            }}
          >
            <span style={{ color: "var(--se-text)" }}>{market}</span>
            <span>{e.legs} legs</span>
            <span
              style={{
                color: (e.rate ?? 0) >= 0.5
                  ? "var(--se-keep-green)"
                  : "var(--se-text-sec)",
              }}
            >
              {pctOf(e.rate)} wr
            </span>
          </div>
        ))
      )}
    </div>
  );
}

function OddsRangeTable(
  { data }: { data: BettingProfileFull["odds_range_performance"] },
): JSX.Element {
  const rows = Object.entries(data);
  return (
    <div
      data-testid="sle-section-a-a3"
      style={{ borderRight: "1px solid var(--se-border)" }}
    >
      <div style={COL_HEAD}>A3 · Odds range</div>
      {rows.length === 0 ? (
        <div style={{ ...CELL, padding: "0 18px 16px" }}>
          No odds-band history yet.
        </div>
      ) : (
        rows.map(([band, e]) => (
          <div
            key={band}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto auto",
              gap: 10,
              padding: "8px 18px",
              borderTop: "1px solid var(--se-border)",
              ...CELL,
            }}
          >
            <span style={{ color: "var(--se-text)" }}>
              {band.replace("_", "–")}
            </span>
            <span>{e.legs}</span>
            <span>{pctOf(e.rate)}</span>
          </div>
        ))
      )}
    </div>
  );
}

function BookmakerTable(
  { data }: { data: BettingProfileFull["bookmaker_performance"] },
): JSX.Element {
  const rows = Object.entries(data);
  return (
    <div data-testid="sle-section-a-a4">
      <div style={COL_HEAD}>A4 · Bookmaker</div>
      {rows.length === 0 ? (
        <div style={{ ...CELL, padding: "0 18px 16px" }}>
          No bookmaker history yet.
        </div>
      ) : (
        rows.map(([book, e]) => (
          <div
            key={book}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto auto",
              gap: 10,
              padding: "8px 18px",
              borderTop: "1px solid var(--se-border)",
              ...CELL,
            }}
          >
            <span style={{ color: "var(--se-text)" }}>{book}</span>
            <span>{e.legs}</span>
            <span
              style={{
                color: (e.clv_pct ?? 0) >= 0
                  ? "var(--se-keep-green)"
                  : "var(--se-drop-red)",
              }}
            >
              {typeof e.clv_pct === "number"
                ? `${e.clv_pct >= 0 ? "+" : ""}${e.clv_pct.toFixed(1)}%`
                : "—"}
            </span>
          </div>
        ))
      )}
    </div>
  );
}

export function SlipExtractorProfile(
  { profile, pro: _pro }: { profile: BettingProfileFull; pro: boolean },
): JSX.Element {
  const archetype = profile.archetype
    ? ARCHETYPE_LABEL[profile.archetype]
    : "Building profile";
  const alerts = profile.pattern_alerts.filter((a) => !a.dismissed);

  return (
    <div
      style={{
        background: "var(--se-surface-card)",
        border: "1px solid var(--se-border)",
        borderRadius: 4,
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "24px 28px 22px",
          borderBottom: "1px solid var(--se-border)",
          display: "grid",
          gridTemplateColumns: "2fr 1fr 1fr 1fr",
          gap: 28,
          alignItems: "flex-end",
        }}
      >
        <div>
          <div
            style={{
              fontFamily: "var(--se-font-mono)",
              fontSize: 10,
              letterSpacing: "0.22em",
              color: "var(--se-text-muted)",
              textTransform: "uppercase",
            }}
          >
            Section A · Profile
          </div>
          <div
            style={{
              fontFamily: "var(--se-font-display)",
              fontSize: 28,
              fontWeight: 500,
              color: "var(--se-text)",
              marginTop: 10,
              letterSpacing: "-0.01em",
            }}
          >
            Your betting intelligence profile.
          </div>
          <div
            style={{
              fontFamily: "var(--se-font-body)",
              fontSize: 13,
              color: "var(--se-text-sec)",
              marginTop: 8,
              lineHeight: 1.55,
              maxWidth: 520,
            }}
          >
            The same intelligence Slip Autopsy builds from your settled
            slips — read forward here to sharpen the next one.
          </div>
        </div>
        <ProfileFigure label="Archetype" value={archetype} />
        <ProfileFigure
          label="Profile confidence"
          value={CONFIDENCE_LABEL[profile.profile_confidence]}
        />
        <ProfileFigure
          label="Process accuracy"
          value={profile.process_accuracy_pct === null
            ? "—"
            : `${profile.process_accuracy_pct}%`}
          sub={`${profile.total_slips} slips · ${profile.total_legs} legs`}
        />
      </div>

      {/* Body — 3 column panels */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.4fr 1fr 1fr",
          alignItems: "stretch",
        }}
      >
        <MarketMatrix data={profile.market_performance} />
        <OddsRangeTable data={profile.odds_range_performance} />
        <BookmakerTable data={profile.bookmaker_performance} />
      </div>

      {/* Pattern alerts — N11: informational only, weight 0. */}
      <div
        data-testid="sle-section-a-a5"
        style={{
          borderTop: "1px solid var(--se-border)",
          padding: "18px 28px",
        }}
      >
        <div
          style={{
            fontFamily: "var(--se-font-mono)",
            fontSize: 10,
            letterSpacing: "0.22em",
            color: "var(--se-text-muted)",
            textTransform: "uppercase",
          }}
        >
          Pattern alerts · N11 honoured · informational only
        </div>
        {alerts.length === 0 ? (
          <div
            style={{
              fontFamily: "var(--se-font-body)",
              fontSize: 12.5,
              color: "var(--se-text-sec)",
              marginTop: 10,
            }}
          >
            No pattern notes yet — they appear here as context, never as
            a score adjustment (N11).
          </div>
        ) : (
          <div
            style={{
              marginTop: 10,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
            }}
          >
            {alerts.map((p) => (
              <div
                key={p.key}
                style={{
                  padding: "12px 14px",
                  background: "var(--se-surface-page)",
                  borderLeft: "1px solid var(--se-text-muted)",
                }}
              >
                <div
                  style={{
                    fontFamily: "var(--se-font-mono)",
                    fontSize: 9.5,
                    letterSpacing: "0.22em",
                    color: "var(--se-text-muted)",
                    textTransform: "uppercase",
                  }}
                >
                  Informational · weight 0 · N11
                </div>
                <div
                  style={{
                    fontFamily: "var(--se-font-body)",
                    fontSize: 13,
                    color: "var(--se-text)",
                    marginTop: 6,
                    fontWeight: 500,
                  }}
                >
                  {p.headline}
                </div>
                <div
                  style={{
                    fontFamily: "var(--se-font-body)",
                    fontSize: 12.5,
                    color: "var(--se-text-sec)",
                    marginTop: 4,
                    lineHeight: 1.5,
                  }}
                >
                  {p.detail}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
